const CACHE_KEY = 'yunliu.invitation.v1';
const TTL = 30 * 86400000;
const CODE = /^[A-HJ-NP-Z2-9]{10}$/;
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function parseInviteCode(value: string): string | null {
  const text = value.trim();
  let candidate = text;
  try { candidate = new URL(text).searchParams.get('invite') ?? ''; } catch { /* plain code */ }
  candidate = candidate.toUpperCase();
  return CODE.test(candidate) ? candidate : null;
}
export function readInvite(storage: StorageLike = localStorage, now = Date.now()): string {
  try {
    const value = JSON.parse(storage.getItem(CACHE_KEY) ?? 'null') as { code: string; savedAt: number } | null;
    if (value && Number.isFinite(value.savedAt) && now >= value.savedAt && now - value.savedAt < TTL && CODE.test(value.code)) return value.code;
    storage.removeItem(CACHE_KEY);
  } catch { /* Storage can be disabled. Registration still supports manual entry. */ }
  return '';
}
export function saveInvite(code: string, storage: StorageLike = localStorage, now = Date.now()): void {
  try {
    if (!code) storage.removeItem(CACHE_KEY);
    else if (CODE.test(code)) storage.setItem(CACHE_KEY, JSON.stringify({ code, savedAt: now }));
  } catch { /* Keep the entered value in the form when storage is unavailable. */ }
}
export function captureInvite(search: string, storage: StorageLike = localStorage): void {
  const code = parseInviteCode(new URLSearchParams(search).get('invite') ?? '');
  // First valid attribution wins; editing the registration field is explicit.
  if (code && !readInvite(storage)) saveInvite(code, storage);
}

let deviceProof: Promise<string> | null = null;
export function browserDeviceProof(issue: (deviceKey: string) => Promise<{ proof: string }>): Promise<string> {
  return deviceProof ??= (async () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
      context.font = '17px Arial';
      context.fillText('Yunliu device / 云流 123', 4, 22);
    }
    // Only the digest leaves the browser. The HttpOnly server cookie persists
    // independently of this fingerprint, which is not hardware attestation.
    const features = [navigator.platform, navigator.hardwareConcurrency, navigator.maxTouchPoints,
      [screen.width, screen.height].sort((a,b)=>a-b).join('x'), screen.colorDepth,
      Intl.DateTimeFormat().resolvedOptions().timeZone, canvas.toDataURL()].join('|');
    const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(features)));
    const key = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return (await issue(key)).proof;
  })().catch(error => { deviceProof = null; throw error; });
}
